import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import {
  setCaptchaStatus,
  setCaptchaToken,
  setForceReset,
} from 'entities/index';
import { BASE, MAIN_PAGE, SCHEDULE_PAGE } from 'shared/consts';
import { useAppDispatch, useAppSelector } from 'shared/lib';

import { useCaptchaLoaded } from '../hooks';

export const InvisibleCaptcha = () => {
  const { token, forceReset, captchaStatus, isCaptchaNeed } = useAppSelector(
    (state) => state.yaSmartCaptchaReducer,
  );
  const dispatch = useAppDispatch();

  const [visible, setVisible] = useState(false);
  const [isTaskOpen, setIsTaskOpen] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRef = useRef(token);
  const captchaStatusRef = useRef(captchaStatus);
  const isTaskOpenRef = useRef(isTaskOpen);

  const locations = [BASE, MAIN_PAGE, SCHEDULE_PAGE];
  const isCaptchaRequiredByPath = locations.includes(window.location.pathname);

  const { isLoaded, errorMessage } = useCaptchaLoaded();

  const executeCaptcha = useCallback(() => {
    dispatch(setCaptchaStatus('loading'));
    setVisible(true);
    setIsTaskOpen(false);

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (
        !tokenRef.current &&
        captchaStatusRef.current !== 'success' &&
        captchaStatusRef.current !== 'criticalError' &&
        !isTaskOpenRef.current
      ) {
        dispatch(setCaptchaStatus('error'));
      } else {
        console.log('HARD EXECUTE SKIPPED - conditions not met');
      }
    }, 3000);
  }, [dispatch, token, captchaStatus, isTaskOpen]);

  useEffect(() => {
    tokenRef.current = token;
    captchaStatusRef.current = captchaStatus;
    isTaskOpenRef.current = isTaskOpen;
  }, [token, captchaStatus, isTaskOpen]);

  useEffect(() => {
    if (
      !token &&
      isLoaded &&
      isCaptchaRequiredByPath &&
      isCaptchaNeed &&
      !visible &&
      captchaStatus === 'wanted'
    ) {
      executeCaptcha();
    }
    if (captchaStatus === 'error') {
      dispatch(setForceReset());
      setVisible(false);
      dispatch(setCaptchaStatus('wanted'));
    }
  }, [token, captchaStatus, isLoaded, visible]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
    } else if (captchaStatus === 'error') {
      console.error('Ошибка каптчи');
    } else if (captchaStatus === 'criticalError') {
      toast.error(
        'Критическая ошибка каптчи. Требуется перезагрузка страницы',
        { duration: Infinity },
      );
    }
  }, [errorMessage, captchaStatus]);

  const handleSuccess = (token: string) => {
    dispatch(setCaptchaToken(token));
    dispatch(setCaptchaStatus('success'));
    setVisible(false);
    setIsTaskOpen(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleChallengeHidden = () => {
    setVisible(false);
    setIsTaskOpen(false);
    if (!token && (captchaStatus === 'loading' || captchaStatus === 'error')) {
      dispatch(setCaptchaStatus('wanted'));
    }
  };

  const handleChallengeVisible = () => {
    setIsTaskOpen(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleNetworkError = () => {
    dispatch(setCaptchaStatus('criticalError'));
  };

  const handleJavascriptError = () => {
    dispatch(setCaptchaStatus('criticalError'));
  };

  return (
    <>
      {isCaptchaRequiredByPath && isCaptchaNeed && (
        <InvisibleSmartCaptcha
          hideShield={false}
          key={String(forceReset)}
          sitekey="ysc1_LnqzLIVZjyYKymR9pmvd1w6npvzaE56milZk0Mqfea97bf7f"
          onSuccess={handleSuccess}
          onChallengeHidden={handleChallengeHidden}
          onChallengeVisible={handleChallengeVisible}
          onNetworkError={handleNetworkError}
          onJavascriptError={handleJavascriptError}
          visible={visible}
        />
      )}
    </>
  );
};
